/**
 * AST Utility Functions
 * Helper functions for creating, manipulating, and working with AST structures
 */

import {
  AstSelection,
  Block,
  BlockAttrs,
  ContentAST,
  DividerBlock,
  EmbedBlock,
  EmbedReference,
  EncryptedNode,
  HeadingBlock,
  ImageBlock,
  InlineContent,
  InlineEmbedNode,
  LinkNode,
  ListBlock,
  ListItem,
  ParagraphBlock,
  QuoteBlock,
  TextDirection,
  TextNode,
} from './astTypes'
import { isEmbedReference, isInlineEmbedReference } from './embedReference'
import { generateBlockId, generateEncryptionId } from './idGenerator'

// Re-export ID generators as part of the AST public API
export { generateBlockId, generateEncryptionId }

/**
 * Creates an empty AST structure
 */
export const createEmptyAST = (lang: 'he' | 'en' | 'mixed' = 'en', dir: TextDirection = 'ltr'): ContentAST => {
  return {
    version: 1,
    meta: {
      lang,
      dir,
    },
    blocks: [createEmptyParagraph()],
  }
}

/**
 * Creates an empty paragraph block
 */
export const createEmptyParagraph = (attrs?: BlockAttrs): ParagraphBlock => {
  return {
    type: 'paragraph',
    id: generateBlockId(),
    content: [createTextNode('')],
    attrs,
  }
}

/**
 * Creates a heading block
 */
export const createHeading = (level: 1 | 2 | 3 | 4 | 5 | 6, text: string = '', attrs?: BlockAttrs): HeadingBlock => {
  return {
    type: 'heading',
    id: generateBlockId(),
    level,
    content: text ? [createTextNode(text)] : [],
    attrs,
  }
}

/**
 * Creates a paragraph block
 */
export const createParagraph = (text: string = '', attrs?: BlockAttrs): ParagraphBlock => {
  return {
    type: 'paragraph',
    id: generateBlockId(),
    content: text ? [createTextNode(text)] : [],
    attrs,
  }
}

/**
 * Creates a list block
 */
export const createList = (ordered: boolean = false, attrs?: Pick<BlockAttrs, 'dir'>): ListBlock => {
  return {
    type: 'list',
    id: generateBlockId(),
    ordered,
    items: [createListItem()],
    attrs,
  }
}

/**
 * Creates a list item
 */
export const createListItem = (text: string = '', attrs?: BlockAttrs): ListItem => {
  return {
    id: generateBlockId(),
    content: text ? [createTextNode(text)] : [],
    attrs,
  }
}

/**
 * Creates a quote block
 */
export const createQuote = (text: string = '', attrs?: BlockAttrs): QuoteBlock => {
  return {
    type: 'quote',
    id: generateBlockId(),
    content: text ? [createTextNode(text)] : [],
    attrs,
  }
}

/**
 * Creates a divider block
 */
export const createDivider = (attrs?: Pick<BlockAttrs, 'align'>): DividerBlock => {
  return {
    type: 'divider',
    id: generateBlockId(),
    attrs,
  }
}

/** Create a read-only source embed without copying the referenced content. */
export const createEmbed = (source: EmbedReference): EmbedBlock => {
  if (!isEmbedReference(source)) {
    throw new Error('Invalid embed reference')
  }
  return { type: 'embed', id: generateBlockId(), source: { ...source } }
}

export const createInlineEmbed = (source: EmbedReference): InlineEmbedNode => {
  if (!isInlineEmbedReference(source)) {
    throw new Error('Invalid inline embed reference')
  }
  return { type: 'inline-embed', source: { ...source } }
}

/**
 * Creates an image block
 */
export const createImage = (
  url: string,
  options?: {
    alt?: string
    width?: number
    height?: number
    caption?: InlineContent[]
    attrs?: Pick<BlockAttrs, 'align'>
  },
): ImageBlock => {
  return {
    type: 'image',
    id: generateBlockId(),
    url,
    alt: options?.alt,
    width: options?.width,
    height: options?.height,
    caption: options?.caption,
    attrs: options?.attrs,
  }
}

/**
 * Creates a text node
 */
export const createTextNode = (text: string, marks?: TextNode['marks']): TextNode => {
  const node: TextNode = {
    type: 'text',
    text,
  }

  if (marks && marks.length > 0) {
    node.marks = marks
  }

  return node
}

/**
 * Creates an encrypted node
 */
export const createEncryptedNode = (
  content: TextNode[],
  id?: string,
  consent?: { character?: string; authorHeld?: true; released?: true },
): EncryptedNode => {
  return {
    type: 'encrypted',
    id: id || generateEncryptionId(),
    algo: 'aes-gcm',
    version: 1,
    content,
    ...(consent?.character ? { character: consent.character } : {}),
    ...(consent?.authorHeld ? { authorHeld: true as const } : {}),
    ...(consent?.released ? { released: true as const } : {}),
  }
}

/**
 * Creates a link node
 */
export const createLinkNode = (url: string, text: string, marks?: TextNode['marks']): LinkNode => {
  return {
    type: 'link',
    url,
    content: [createTextNode(text, marks)],
  }
}

/**
 * Finds a top-level block by ID in the AST
 */
export const findBlockById = (ast: ContentAST, blockId: string): Block | null => {
  return ast.blocks.find((block) => block.id === blockId) || null
}

/**
 * Finds a list item by ID in the AST (searches recursively through all lists)
 */
export const findListItemById = (ast: ContentAST, itemId: string): ListItem | null => {
  for (const block of ast.blocks) {
    if (block.type === 'list') {
      const foundItem = findInListItems(block.items, itemId)
      if (foundItem) {
        return foundItem
      }
    }
  }
  return null
}

/**
 * Finds any block or list item by ID in the AST
 */
export const findAnyById = (ast: ContentAST, id: string): Block | ListItem | null => {
  // First try to find as a top-level block
  const block = findBlockById(ast, id)
  if (block) {
    return block
  }

  // Then try to find as a list item
  return findListItemById(ast, id)
}

/**
 * Helper function to search in list items recursively
 */
const findInListItems = (items: ListItem[], itemId: string): ListItem | null => {
  for (const item of items) {
    if (item.id === itemId) {
      return item
    }

    if (item.children) {
      const foundInChildren = findInListItems(item.children, itemId)
      if (foundInChildren) {
        return foundInChildren
      }
    }
  }

  return null
}

/**
 * Gets the index of a block in the AST
 */
export const getBlockIndex = (ast: ContentAST, blockId: string): number => {
  return ast.blocks.findIndex((block) => block.id === blockId)
}

/**
 * Gets all text content from a block as a plain string
 */
export const getBlockText = (block: Block): string => {
  if (block.type === 'divider') {
    return ''
  }

  if ('content' in block) {
    return getInlineText(block.content)
  }

  if (block.type === 'list') {
    return block.items.map((item) => getListItemText(item)).join('\n')
  }

  return ''
}

/**
 * Gets text content from list item recursively
 */
const getListItemText = (item: ListItem): string => {
  let text = getInlineText(item.content)

  if (item.children && item.children.length > 0) {
    const childText = item.children.map((child) => getListItemText(child)).join('\n')
    text += '\n' + childText
  }

  return text
}

/**
 * Gets text content from inline content array
 */
export const getInlineText = (content: InlineContent[]): string => {
  return content
    .map((node) => {
      if (node.type === 'text') {
        return node.text
      }
      if (node.type === 'encrypted') {
        return getInlineText(node.content)
      }
      if (node.type === 'link') {
        return getInlineText(node.content)
      }
      if (node.type === 'inline-embed') {
        // One opaque object keeps range offsets and private-note stripping correct
        // without putting resolved (potentially private) source text in this AST.
        return '\uFFFC'
      }
      return ''
    })
    .join('')
}

/**
 * Gets all text content from an AST as plain text
 */
export const getText = (ast: ContentAST): string => {
  if (!ast || !ast.blocks) {
    return ''
  }

  return ast.blocks
    .map((block) => getBlockText(block))
    .filter((text) => text.trim() !== '')
    .join('\n\n')
}

/**
 * Finds a heading block by its text content
 */
export const findHeadingByText = (ast: ContentAST, headingText: string): HeadingBlock | null => {
  for (const block of ast.blocks) {
    if (block.type === 'heading') {
      const blockText = getInlineText(block.content).trim()
      if (blockText === headingText.trim()) {
        return block
      }
    }
  }
  return null
}

/**
 * Checks if a block is empty (has no meaningful content)
 */
export const isEmptyBlock = (block: Block): boolean => {
  if (block.type === 'divider' || block.type === 'embed') {
    return false // Non-text blocks still carry meaningful document content.
  }

  const text = getBlockText(block).trim()
  return text.length === 0
}

/**
 * Checks if an AST contains only empty content
 */
export const isEmptyAST = (ast: ContentAST): boolean => {
  return ast.blocks.every((block) => isEmptyBlock(block))
}

/**
 * Creates a deep copy of an AST (for immutable operations)
 */
export const cloneAST = (ast: ContentAST): ContentAST => {
  return JSON.parse(JSON.stringify(ast))
}

/**
 * Creates a deep copy of a block
 */
export const cloneBlock = (block: Block): Block => {
  return JSON.parse(JSON.stringify(block))
}

/**
 * Counts total characters in AST (excluding encryption markers)
 */
export const getASTCharCount = (ast: ContentAST): number => {
  return ast.blocks.reduce((total, block) => {
    return total + getBlockText(block).length
  }, 0)
}

/**
 * Counts total words in AST
 */
export const getASTWordCount = (ast: ContentAST): number => {
  const text = ast.blocks.map((block) => getBlockText(block)).join(' ')
  return text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length
}

/**
 * Gets all encryption IDs used in the AST
 */
export const getEncryptionIds = (ast: ContentAST): string[] => {
  const ids: string[] = []

  const extractFromInlineContent = (content: InlineContent[]) => {
    content.forEach((node) => {
      if (node.type === 'encrypted') {
        ids.push(node.id)
        extractFromInlineContent(node.content)
      }
    })
  }

  const extractFromListItems = (items: ListItem[]) => {
    items.forEach((item) => {
      extractFromInlineContent(item.content)
      if (item.children) {
        extractFromListItems(item.children)
      }
    })
  }

  ast.blocks.forEach((block) => {
    if ('content' in block) {
      extractFromInlineContent(block.content)
    } else if (block.type === 'list') {
      extractFromListItems(block.items)
    }
  })

  // Remove duplicates manually to avoid Set iteration issue
  return ids.filter((id, index) => ids.indexOf(id) === index)
}

/**
 * Gets all block IDs in the AST
 */
export const getAllBlockIds = (ast: ContentAST): string[] => {
  const ids: string[] = []

  const extractFromListItems = (items: ListItem[]) => {
    items.forEach((item) => {
      ids.push(item.id)
      if (item.children) {
        extractFromListItems(item.children)
      }
    })
  }

  ast.blocks.forEach((block) => {
    ids.push(block.id)
    if (block.type === 'list') {
      extractFromListItems(block.items)
    }
  })

  return ids
}

/**
 * Validates that a selection is within bounds of the AST
 */
export const isValidSelection = (ast: ContentAST, selection: AstSelection): boolean => {
  const block = findBlockById(ast, selection.blockId)
  if (!block) {
    return false
  }

  const blockText = getBlockText(block)
  return selection.offset >= 0 && selection.offset <= blockText.length
}

/**
 * Converts a selection to a range selection with length
 */
export const selectionToRange = (selection: AstSelection, length: number): AstSelection => {
  return {
    ...selection,
    length: Math.max(0, length),
  }
}

/**
 * Merges adjacent text nodes with identical marks
 */
export const normalizeInlineContent = (content: InlineContent[]): InlineContent[] => {
  const result: InlineContent[] = []

  for (const node of content) {
    if (node.type === 'text' && result.length > 0) {
      const lastNode = result[result.length - 1]

      if (lastNode.type === 'text' && JSON.stringify(lastNode.marks || []) === JSON.stringify(node.marks || [])) {
        // Merge with previous text node. Construct a new object instead of mutating —
        // the AST may be frozen by immer (post-operation state is always frozen),
        // and mutation would throw "Cannot assign to read only property".
        result[result.length - 1] = { ...lastNode, text: lastNode.text + node.text }
        continue
      }
    }

    if (node.type === 'encrypted') {
      // Normalize content within encrypted nodes
      result.push({
        ...node,
        content: normalizeInlineContent(node.content) as TextNode[],
      })
    } else {
      result.push(node)
    }
  }

  return result
}

/**
 * Normalizes the entire AST by merging adjacent text nodes
 */
export const normalizeAST = (ast: ContentAST): ContentAST => {
  const normalizeListItems = (items: ListItem[]): ListItem[] => {
    return items.map((item) => ({
      ...item,
      content: normalizeInlineContent(item.content),
      children: item.children ? normalizeListItems(item.children) : undefined,
    }))
  }

  const normalizedBlocks = ast.blocks.map((block) => {
    if ('content' in block) {
      return {
        ...block,
        content: normalizeInlineContent(block.content),
      }
    }

    if (block.type === 'list') {
      return {
        ...block,
        items: normalizeListItems(block.items),
      }
    }

    return block
  })

  return {
    ...ast,
    blocks: normalizedBlocks,
  }
}
